import cx from 'clsx';
import React, { useRef } from 'react';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { ReactComponent as TableSort } from '@assets/svg/table-sort.svg';

import ButtonGoUp from '@components/common/ButtonGoUp/ButtonGoUp';
import TableEmptyRow from '@components/common/TableEmptyRow/TableEmptyRow';
import TooltipSort from '@components/common/TooltipSort/TooltipSort';

import { initialSorting } from '@constants/tableSorting';

import useTranslation from '@hooks/useTranslation';

import { useAssetTreeQuery } from '@services/useAssetTreeQuery';
import { useCustomerInfoQuery } from '@services/useCustomerInfoQuery';

import { selectAppLocationSelectValue } from '@store/useAppStore';

import type { ICostSavingDetailsByMonth } from '../../model/costSavingDetails';
import { selectCostSavingsSorting, useCostSavingsActions } from '../../store/useCostSavingsStore';
import ExpandedRow from '../ExpandedRow/ExpandedRow';
import CostSavingsTableSkeleton from './CostSavingsTableSkeleton';
import style from './style.module.scss';

interface IProps {
  isLoading: boolean;
  data: ICostSavingDetailsByMonth[];
}

const CostSavingsTable: React.FC<IProps> = ({ isLoading, data }) => {
  const { customerId } = useParams();
  const locationSelectValue = selectAppLocationSelectValue();
  const sorting = selectCostSavingsSorting();
  const costSavingsTableContainerRef = useRef(null);

  const { t } = useTranslation();

  const { setCostSavingsSorting } = useCostSavingsActions();

  const { data: customerInfoData } = useCustomerInfoQuery({
    customerId,
    locationIds:
      locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue?.filterIds,
  });
  const { data: assetTreeData } = useAssetTreeQuery();

  const onChangeSorting = (key: string) => {
    if (!sorting.id || sorting.id !== key) {
      setCostSavingsSorting({ id: key, desc: false });
      return;
    }
    const { id, desc } = sorting;
    if (desc) {
      setCostSavingsSorting(initialSorting);
    } else {
      setCostSavingsSorting({ id, desc: true });
    }
  };

  const isSelectedAllFacility = locationSelectValue.id === 'all';

  const currentCurrency = isSelectedAllFacility
    ? assetTreeData?.customerCurrency
    : locationSelectValue.currency;

  return (
    <>
      <div className={style.costSavingsTableWrapper} ref={costSavingsTableContainerRef}>
        <table className={style.costSavingsTable}>
          <thead>
            <tr>
              <th className={style.dateColumn}>
                {t('Date')}
                <TableSort
                  className={cx(style.sortIcon, { [style.activeSort]: sorting.id === 'month_name' })}
                  onClick={() => onChangeSorting('month_name')}
                  data-tooltip-content="month_name"
                  data-tooltip-id="cost-savings-sort-tooltip"
                />
              </th>
              {customerInfoData?.showUnplannedDowntime && (
                <th>
                  <div className={style.headCellContainer}>
                    <span className={style.flexHeader}>{t('Unplanned downtime avoided, hrs')}</span>
                    <TableSort
                      className={cx(style.sortIcon, {
                        [style.activeSort]: sorting.id === 'downtime_avoided',
                      })}
                      onClick={() => onChangeSorting('downtime_avoided')}
                      data-tooltip-content="downtime_avoided"
                      data-tooltip-id="cost-savings-sort-tooltip"
                    />
                  </div>
                </th>
              )}
              <th>
                <div className={style.headCellContainer}>
                  <span className={style.flexHeader}>{`${t('Equipment/labor Savings')}, ${
                    currentCurrency.symbol
                  }`}</span>
                  <TableSort
                    className={cx(style.sortIcon, {
                      [style.activeSort]: sorting.id === 'equipment_labor_savings',
                    })}
                    onClick={() => onChangeSorting('equipment_labor_savings')}
                    data-tooltip-content="equipment_labor_savings"
                    data-tooltip-id="cost-savings-sort-tooltip"
                  />
                </div>
              </th>
              {customerInfoData?.showTotalCostSavings && (
                <th>
                  {`${t('total savings')}, ${currentCurrency.symbol}`}
                  <TableSort
                    className={cx(style.sortIcon, { [style.activeSort]: sorting.id === 'cost_avoided' })}
                    onClick={() => onChangeSorting('cost_avoided')}
                    data-tooltip-content="cost_avoided"
                    data-tooltip-id="cost-savings-sort-tooltip"
                  />
                </th>
              )}
              {customerInfoData?.showCompletionReports && (
                <th>
                  <div className={style.headCellContainer}>
                    <span className={style.flexHeader}>{t('Completion reports submitted')}</span>
                    <TableSort
                      className={cx(style.sortIcon, { [style.activeSort]: sorting.id === 'reports_count' })}
                      onClick={() => onChangeSorting('reports_count')}
                      data-tooltip-content="reports_count"
                      data-tooltip-id="cost-savings-sort-tooltip"
                    />
                  </div>
                </th>
              )}
              <th />
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <CostSavingsTableSkeleton />
            ) : data?.length ? (
              data?.map((row) => <ExpandedRow key={uuidv4()} row={row} />)
            ) : (
              <TableEmptyRow text={t('No cost savings details were found.')} />
            )}
          </tbody>
        </table>
        <TooltipSort id="cost-savings-sort-tooltip" sorting={sorting} />
      </div>
      <div className={style.tableFooter}>
        <ButtonGoUp ref={costSavingsTableContainerRef} />
      </div>
    </>
  );
};

export default CostSavingsTable;
